/**
 * Pure filter + display helpers for the conversations-V2 room list.
 *
 * Extracted from conversationsV2.js so the logic can be unit-tested.
 * This logic decides whether a Firestore conversation parent doc should appear
 * in the SMS Conversations chat list.
 *
 * Two reasons a room is hidden:
 *
 *   1. No real preview content + no patient response.
 *      Covers reminder-only conversations (after the 2026-05-21 backend gate
 *      stops writing lastMessage for automated messages) AND empty placeholders
 *      from onConversationAdded that never got a real message.
 *
 *   2. Legacy stale data: lastMessage.attributes has reminderType from BEFORE
 *      the backend gate deployed, and patient hasn't responded. Kept as
 *      defense-in-depth for sites with old data still in Firestore.
 *
 * NOTE: this file uses ESM `export` syntax so it works cleanly with the
 * vue-cli-service webpack build (which treats imported .js files as ESM).
 * The node:test specs in __tests__/conversationsV2-filters.test.js MIRROR
 * this logic rather than cross-importing it — same pattern nudges.test.js
 * uses to avoid CommonJS/ESM interop headaches. If you change anything here,
 * also update the mirror in the test file.
 */

export function parseAttrs(raw) {
  try {
    if (raw === null || raw === undefined) return {};
    if (typeof raw === 'string') return JSON.parse(raw);
    if (typeof raw === 'object') return raw;
    return {};
  } catch (_) {
    return {};
  }
}

/** Shown for an ALREADY-VISIBLE room that has no usable preview text. */
export const BLANK_PREVIEW_PLACEHOLDER = '(no preview)';

/**
 * Backstop for rooms whose `lastMessage.content` was persisted EMPTY.
 *
 * Prod bug (Smile Bar, 2026-07-31): a photo-only inbound MMS whose S3 re-host failed was written
 * with content:''. That does NOT hide the room — an inbound-authored last message computes
 * unreadCount=1, which defeats Rule 1's "no content AND no unread" — so the row rendered with the
 * patient's name, an unread badge and NO preview text at all. Staff had no idea a message existed.
 *
 * Fixed at the source in the layer (smsEngine.previewContent now writes '[Media Message]' /
 * '[Empty Message]'), but Firestore docs written before that fix keep their empty content for the
 * remainder of the 21-day retention window — this covers them.
 *
 * MUST be applied AFTER shouldHideRoomFromChatList, never before. Filling content in first would
 * make hasRealContent true and un-hide every empty-placeholder room the filter deliberately
 * suppresses (regressing the 2026-05-21 "Tanya #2" fix). Applied afterwards it is purely
 * cosmetic: it changes what a VISIBLE row says, never which rows are visible.
 *
 * We deliberately do NOT guess '[Media Message]' here — the parent doc carries no media hint, and
 * inventing one would mislabel a plain text message. A neutral marker tells staff "there is
 * something here, open it", honestly.
 *
 * @param {string} preview - content/text/message already resolved by the caller.
 * @returns {string} the preview to display for a visible room; never empty.
 */
export function previewForDisplay(preview) {
  const text = typeof preview === 'string' ? preview : '';
  return text.trim() !== '' ? text : BLANK_PREVIEW_PLACEHOLDER;
}

/**
 * Client-computed unread badge for a conversation list row.
 *
 * Unread means unviewed inbound exists — NOT "the last message is inbound".
 * Manual outbound (Patient Profile, Conversations composer, workflow, wiki, form,
 * campaign) replaces lastMessage with attributes.outbound=true but does NOT write
 * siteLastReadAt. Using lastBySite as the unread gate made those sends look like
 * the inbound had been read (Calvin / Nilay, 2026-09).
 *
 * lastInboundAtMs is written by smsEngine on inbound only. Compare it to
 * siteLastReadAt / local lastRead / unreadBaselineMs. Opening the thread
 * (updateConversationReadStatus) is still the only thing that should clear unread.
 *
 * Docs without lastInboundAtMs (older than ~21-day retention) fall back to the
 * previous lastBySite / lastMessage-timestamp logic so we don't invent unread
 * on outbound-only threads.
 *
 * @param {object} args
 * @param {number|null|undefined} args.lastInboundAtMs
 * @param {number|null|undefined} args.lastMessageTs
 * @param {boolean} args.lastBySite
 * @param {number|null|undefined} args.lastRead
 * @param {number|null|undefined} args.baseline
 * @param {boolean} args.isSelected
 * @param {number|null|undefined} args.markedUnreadAt
 * @param {number} [args.serverUnread]
 * @returns {0|1}
 */
export function computeRoomUnreadCount({
  lastInboundAtMs,
  lastMessageTs,
  lastBySite,
  lastRead,
  baseline,
  isSelected,
  markedUnreadAt,
  serverUnread,
}) {
  if (isSelected) return 0;
  if (markedUnreadAt && (!lastRead || markedUnreadAt > lastRead)) return 1;

  const inboundMs = Number.isFinite(lastInboundAtMs) ? lastInboundAtMs : null;
  if (inboundMs != null) {
    const inboundAfterBaseline = !(baseline && inboundMs <= baseline);
    const inboundAfterLastRead = !(lastRead && inboundMs <= lastRead);
    return inboundAfterBaseline && inboundAfterLastRead ? 1 : 0;
  }

  // Legacy fallback: no lastInboundAtMs on the doc.
  const ts = lastMessageTs;
  const afterBaseline = !(baseline && ts && ts <= baseline);
  const afterLastRead = !(lastRead && ts && ts <= lastRead);
  if (afterBaseline && afterLastRead && !lastBySite) return 1;
  const hasAnyRead = Number.isFinite(lastRead) || Number.isFinite(baseline);
  if (!hasAnyRead && serverUnread > 0 && !lastBySite) return 1;
  return 0;
}

/**
 * @param {Object} data - The Firestore conversation parent doc (already loaded into a JS object).
 * @returns {boolean} true if the room should be skipped from the chat list.
 */
export function shouldHideRoomFromChatList(data) {
  if (!data) return true; // defensive

  // Merged duplicate thread: its messages now live in the canonical thread, so hide the
  // orphan from the list (set by tools/merge-duplicate-threads.js; reversible — clearing
  // the field un-hides it). KEEP IN SYNC with the test mirror.
  if (data.mergedInto) return true;

  const lastMessage = data.lastMessage;
  const lastMessageAttributes = parseAttrs(lastMessage && lastMessage.attributes);
  const hasRealContent =
    !!lastMessage &&
    typeof lastMessage.content === 'string' &&
    lastMessage.content.length > 0;
  const hasUnread = !!data.unreadCount && data.unreadCount > 0;

  // Rule 1 (new — Tanya #2 fix): hide rooms with no real preview content AND no
  // patient response. Reminder-only conversations now have no lastMessage at all
  // (backend gate at 2026-05-21), so they fall into this case. Also catches
  // empty placeholders from onConversationAdded that never got a real message.
  if (!hasRealContent && !hasUnread) return true;

  // Rule 2 (legacy — defense-in-depth): hide rooms where lastMessage has a
  // stale reminderType attribute from before the backend gate deployed.
  // Becomes a no-op once all old Firestore docs cycle out (21-day retention).
  const isManualStaffSend =
    !!lastMessageAttributes.sentViaAPI ||
    (!!lastMessageAttributes.outbound && !lastMessageAttributes.reminderType);
  if (
    lastMessage &&
    lastMessageAttributes.reminderType &&
    !isManualStaffSend &&
    !hasUnread
  ) {
    return true;
  }

  return false;
}
